import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  model,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

let uiPhoneInputUid = 0;

export interface UiCallingCode {
  code: string;
  label: string;
}

export const UI_CALLING_CODES: UiCallingCode[] = [
  { code: '+52', label: 'México' },
  { code: '+1', label: 'EE.UU. / Canadá' },
  { code: '+54', label: 'Argentina' },
  { code: '+55', label: 'Brasil' },
  { code: '+56', label: 'Chile' },
  { code: '+57', label: 'Colombia' },
  { code: '+51', label: 'Perú' },
  { code: '+58', label: 'Venezuela' },
  { code: '+591', label: 'Bolivia' },
  { code: '+593', label: 'Ecuador' },
  { code: '+502', label: 'Guatemala' },
  { code: '+504', label: 'Honduras' },
  { code: '+505', label: 'Nicaragua' },
  { code: '+506', label: 'Costa Rica' },
  { code: '+503', label: 'El Salvador' },
  { code: '+507', label: 'Panamá' },
  { code: '+595', label: 'Paraguay' },
  { code: '+598', label: 'Uruguay' },
  { code: '+34', label: 'España' },
  { code: '+44', label: 'Reino Unido' },
  { code: '+33', label: 'Francia' },
  { code: '+49', label: 'Alemania' },
  { code: '+39', label: 'Italia' },
  { code: '+81', label: 'Japón' },
  { code: '+86', label: 'China' },
];

/**
 * Átomo: campo de teléfono con selector de lada custom (dropdown igual a ui-select)
 * y un input para el número. Sin bandera, solo el código.
 */
@Component({
  selector: 'ui-phone-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
  host: {
    class: 'ui-phone-input-host',
    '[class.ui-phone-input-host--block]': 'block()',
  },
})
export class UiPhoneInputComponent {
  @ViewChild('ladaTrigger') private ladaTrigger!: ElementRef<HTMLButtonElement>;
  @ViewChild('ladaSearch') private ladaSearch?: ElementRef<HTMLInputElement>;

  readonly label = input('Teléfono');
  readonly callingCode = model<string>('+52');
  readonly phoneNumber = model<string>('');
  readonly placeholder = input('0000000000');

  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);

  readonly name = input<string | undefined>(undefined);
  readonly block = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  readonly callingCodeChange = output<string>();
  readonly phoneNumberChange = output<string>();
  readonly blurred = output<FocusEvent>();
  readonly focused = output<FocusEvent>();

  protected readonly callingCodes = UI_CALLING_CODES;

  protected readonly generatedId = `ui-phone-input-${++uiPhoneInputUid}`;
  protected readonly inputId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly ladaPanelId = computed(() => `${this.generatedId}-lada-panel`);
  protected readonly messageId = computed(() => `${this.inputId()}-message`);

  protected readonly ariaDescribedBy = computed(() =>
    this.message() ? this.messageId() : null,
  );
  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );
  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected readonly ladaOpen = signal(false);
  protected readonly ladaSearch_ = signal('');

  protected readonly panelRect = signal<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  protected readonly filteredCodes = computed(() => {
    const q = this.ladaSearch_().toLowerCase().trim();
    if (!q) return this.callingCodes;
    return this.callingCodes.filter(
      (c) => c.code.includes(q) || c.label.toLowerCase().includes(q),
    );
  });

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    const panel = document.getElementById(this.ladaPanelId());
    if (
      !this.ladaTrigger?.nativeElement.contains(target) &&
      !(panel?.contains(target) ?? false)
    ) {
      this.closeLada();
    }
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.closeLada();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onScrollOrResize(): void {
    if (this.ladaOpen()) this.closeLada();
  }

  protected toggleLada(): void {
    if (this.disabled()) return;
    this.ladaOpen() ? this.closeLada() : this.openLada();
  }

  protected openLada(): void {
    const rect = this.ladaTrigger.nativeElement.getBoundingClientRect();
    this.panelRect.set({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 240) });
    this.ladaSearch_.set('');
    this.ladaOpen.set(true);
    setTimeout(() => this.ladaSearch?.nativeElement?.focus(), 0);
  }

  protected closeLada(): void {
    if (!this.ladaOpen()) return;
    this.ladaOpen.set(false);
    this.panelRect.set(null);
  }

  protected selectCode(code: string): void {
    this.callingCode.set(code);
    this.callingCodeChange.emit(code);
    this.closeLada();
  }

  protected onSearchChange(q: string): void {
    this.ladaSearch_.set(q);
  }

  protected onNumberInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.phoneNumber.set(v);
    this.phoneNumberChange.emit(v);
  }

  protected onBlur(event: FocusEvent): void {
    this.blurred.emit(event);
  }

  protected onFocus(event: FocusEvent): void {
    this.focused.emit(event);
  }
}
